import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
  buildWebPageSchema,
} from "@/lib/seo";
import { FABCHAT_FEATURE_LIST } from "@/lib/geo-product";
import { faqs } from "@/components/landing/FAQ";
import { GEO_FEATURE_PAGES } from "./geo-feature-pages";

const TITLE = "Resumo da FabChat para IA | FabChat";
const DESCRIPTION =
  "Resumo factual da FabChat: agentes de IA no WhatsApp e Instagram, CRM, disparos (API oficial e QR), catálogo, tarefas e recuperação de lead.";

const PATH = "/llm";

const CHANNEL_LINKS = [
  { to: "/whatsapp/agente-ia", label: "Agente de IA no WhatsApp" },
  { to: "/instagram/agente-ia", label: "Agente de IA no Instagram (DM e comentários)" },
  { to: "/whatsapp/disparos", label: "Disparos: API oficial da Meta e conexão por QR" },
  { to: "/integracoes/crm", label: "CRM nativo" },
  { to: "/planos", label: "Planos" },
];

export default function LlmGeoPage() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, [
    { name: "Início", path: "/" },
    { name: "Resumo para IA", path: PATH },
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath={PATH} />
      <StructuredData
        data={[
          buildOrganizationSchema(baseUrl),
          buildSoftwareApplicationSchema(baseUrl, DESCRIPTION),
          breadcrumbSchema,
          buildFAQPageSchema(faqs),
          buildWebPageSchema(baseUrl, { path: PATH, title: TITLE, description: DESCRIPTION }),
        ]}
      />
      <Header />

      <main className="flex-1">
        <article className="container mx-auto max-w-3xl px-4 py-16 md:py-24">
          <h1 className="text-4xl font-bold tracking-tight text-balance md:text-5xl">O que é a FabChat</h1>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            A FabChat é um SaaS para criar e operar agentes de IA no WhatsApp Business e no Instagram
            (Direct e comentários), com CRM nativo, catálogo, disparos, tarefas e recursos de operação
            comercial no mesmo painel.
          </p>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold tracking-tight">WhatsApp: API oficial e QR</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-muted-foreground">
              <li>
                <strong className="text-foreground">API oficial (Cloud API da Meta):</strong> número ligado
                pela conta Business. Campanhas seguem as regras da WhatsApp Business Platform, com templates
                quando exigido.
              </li>
              <li>
                <strong className="text-foreground">Conexão por QR Code (não oficial):</strong> vínculo
                rápido, como um segundo aparelho. Não é a API oficial da Meta. Uso em massa aumenta o risco
                de restrição da conta.
              </li>
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold tracking-tight">Capacidades</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-muted-foreground">
              {FABCHAT_FEATURE_LIST.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <nav className="mt-6 flex flex-col gap-2 text-sm" aria-label="Páginas de produto">
              {CHANNEL_LINKS.map((item) => (
                <Link key={item.to} to={item.to} className="text-primary hover:underline">
                  {item.label}
                </Link>
              ))}
              {GEO_FEATURE_PAGES.filter((page) => page.path !== "/whatsapp/disparos").map((page) => (
                <Link key={page.path} to={page.path} className="text-primary hover:underline">
                  {page.h1}
                </Link>
              ))}
            </nav>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold tracking-tight">Perguntas frequentes</h2>
            <dl className="mt-4 space-y-6">
              {faqs.map((item) => (
                <div key={item.question}>
                  <dt className="font-medium text-foreground">{item.question}</dt>
                  <dd className="mt-1 text-muted-foreground">{item.answer}</dd>
                </div>
              ))}
            </dl>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
